import React, {FC} from "react"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import PropTypes from "prop-types"
import Flex from "@irati/ui/src/Flex"
import {H4} from "@irati/ui/src/Typography"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import adminTracking from "@irati-tracking/admin"
import {IconButton} from "@irati/ui/src/Button"
import {
  SETTINGS_CUSTOMISE,
  SETTINGS_CUSTOMISE_BRAND,
  SETTINGS_CUSTOMISE_PERKS_VISIBILITY,
  SETTINGS_CUSTOMISE_BENEFITS,
  SETTINGS_CUSTOMISE_VALUES,
  SETTINGS_CUSTOMISE_CELEBRATIONS,
  SETTINGS_PERMISSIONS_RECOGNITION,
  SETTINGS_LOGIN_SSU,
  SETTINGS_LOGIN_SSO,
  ANALYTICS_BASE,
  ANALYTICS_OVERALL,
  ANALYTICS_PERKS,
  ANALYTICS_CELEBRATIONS} from "@irati-web/admin/src/routes/routeConfig"
import theme from "@irati/ui/src/global/theme"
import authentication from "@irati/auth"

import {StyledLogoContainer} from "./styled"
import AdminRouterLink from "./AdminRouterLink"

interface Context {
  apps: {[key: string]: string},
  appPath: string
}

interface Props extends InjectIntl {
  isCollapsed?: boolean,
  onClick?: () => void
}

const AdminSecondaryNavigationMenuContent: FC<Props> = ({formatMessage, isCollapsed, onClick}, {apps}: Context) => {
  const getIcon = () => {
    if (isRoute(SETTINGS_CUSTOMISE)) {
      return IconTypes.customisation
    }
    if (isRoute(SETTINGS_PERMISSIONS_RECOGNITION) || isRoute(SETTINGS_LOGIN_SSU)) {
      return IconTypes.settings
    }
    if (isRoute(ANALYTICS_BASE)) {
      return IconTypes.analytics
    }
  }

  const getTitle = () => {
    if (isRoute(SETTINGS_CUSTOMISE)) {
      return "platform.adminNavigation.primary.customisation"
    }
    if (isRoute(SETTINGS_PERMISSIONS_RECOGNITION) || isRoute(SETTINGS_LOGIN_SSU)) {
      return "platform.adminNavigation.primary.settings"
    }
    if (isRoute(ANALYTICS_BASE)) {
      return "Reporting"
    }
  }

  const isRoute = (route: string) => {
    const {ADMIN} = apps
    const pathname = window.location.pathname
    return pathname === `${ADMIN}${route}` || pathname.includes(`${ADMIN}${route}`)
  }

  const getLinks = () => {
    if (isRoute(SETTINGS_CUSTOMISE)) {
      return [
        {title: "platform.adminNavigation.secondary.logos",
          id: "logosAndColours",
          to: SETTINGS_CUSTOMISE_BRAND,
          onClick: adminTracking.navigationClickCustomisationsLogo},
        {title: "platform.adminNavigation.secondary.perkVisibility",
          id: "perkVisibility",
          to: SETTINGS_CUSTOMISE_PERKS_VISIBILITY,
          onClick: adminTracking.navigationClickCustomisationsPerkVisibility},
        {title: "platform.adminNavigation.secondary.customBenefits",
          id: "customBenefits",
          to: SETTINGS_CUSTOMISE_BENEFITS,
          onClick: adminTracking.navigationClickCustomisationsCustomBenefits},
        {title: "platform.adminNavigation.secondary.companyValues",
          id: "companyValues",
          to: SETTINGS_CUSTOMISE_VALUES,
          onClick: adminTracking.navigationClickCustomisationsCompanyValues},
        {title: "platform.adminNavigation.secondary.companyCelebrations",
          id: "companyCelebrations",
          to: SETTINGS_CUSTOMISE_CELEBRATIONS,
          onClick: adminTracking.navigationClickCustomisationsCompanyCelebrations}
      ]
    }

    if (isRoute(ANALYTICS_BASE)) {
      return [
        {title: "platform.adminNavigation.secondary.overall",
          id: "overallReporting",
          to: ANALYTICS_OVERALL},
        {title: "platform.adminNavigation.secondary.perks",
          id: "perksReporting",
          to: ANALYTICS_PERKS},
        {title: "platform.adminNavigation.secondary.celebrations",
          id: "celebrationsReporting",
          to: ANALYTICS_CELEBRATIONS}
      ]
    }

    const settings = [
      {title: "platform.adminNavigation.secondary.roles",
        id: "rolesAndPermissions",
        to: SETTINGS_PERMISSIONS_RECOGNITION,
        onClick: adminTracking.navigationClickSettingsRolePermissions},
      {title: "platform.adminNavigation.secondary.ssu",
        id: "selfSignUpSetup",
        to: SETTINGS_LOGIN_SSU,
        onClick: adminTracking.navigationClickSettingsSelfSignup}
    ]

    const showLegacySSOSettings = authentication.apps("admin.sso") && authentication.business().showSsoLogin

    if (showLegacySSOSettings) {
      return [
        ...settings,
        {title: "platform.adminNavigation.secondary.sso",
          id: "singleSignOnLegacy",
          to: SETTINGS_LOGIN_SSO,
          onClick: adminTracking.navigationClickSettingsSelfSignup}
      ]
    }

    return settings
  }

  return (
    <>
      <Flex height="100%" direction="column" padding={[5, 2, 0, 2]}>
        <Icon icon={getIcon()} size={32} />
        {!isCollapsed &&
          <>
            <H4 weight="bold" color={theme.colors.primary2} margin={[2, 0, 5]}>
              {formatMessage({id: getTitle()})}
            </H4>
            {getLinks().map((link) =>
              <AdminRouterLink
                id={link.id}
                to={link.to}
                title={formatMessage({id: link.title})}
                onClick={link.onClick} />
            )}
          </>
        }
        <StyledLogoContainer direction="column">
          <IconButton
            size={32}
            icon={isCollapsed ? IconTypes.expand : IconTypes.collapse}
            onClick={onClick} />
        </StyledLogoContainer>
      </Flex>
    </>
  )
}

AdminSecondaryNavigationMenuContent.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string.isRequired
}

export default injectIntl(AdminSecondaryNavigationMenuContent)
